<template>
  <div>
    <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
      <div v-for="(range, index) in modelValue" :key="index" class="flex items-center gap-2">
        <Input
          :model-value="minuteOfDayToTimeString(range.start)"
          type="time"
          class="w-32"
          :is-error="!!error"
          @update:model-value="handleTimeChange(index, 'start', $event)"
        />
        <span class="text-gray-500">–</span>
        <Input
          :model-value="minuteOfDayToTimeString(range.end)"
          type="time"
          class="w-32"
          :is-error="!!error"
          @update:model-value="handleTimeChange(index, 'end', $event)"
        />
        <CVButton
          v-if="modelValue.length > 1"
          variant="ghost"
          size="iconXs"
          :title="t('removeRange')"
          @click.prevent="handleRemoveRange(index)"
        >
          <XIcon class="size-4" />
        </CVButton>
      </div>
      <CVButton variant="link" size="sm" :disabled="!nextRange" @click.prevent="handleAddRange">
        {{ t('addRange') }}
      </CVButton>
    </div>
    <InputHint :hint="error ? t(error) : undefined" is-error />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { XIcon } from 'lucide-vue-next';
import type { TimeRange } from '@carvia/ros-client-types';
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import InputHint from '@/components/InputHint.vue';
import { findTimeRangesError } from '@/utils/validate-time-ranges';
import { minuteOfDayToTimeString, timeStringToMinuteOfDay } from '@/utils/convert-opening-hours';

const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

const props = defineProps<{
  modelValue: TimeRange[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: TimeRange[]): void;
}>();

const { t } = useI18n();

const error = computed(() => findTimeRangesError(props.modelValue));

/** A new range one hour after the latest range, e.g. after a lunch break. */
const nextRange = computed((): TimeRange | null => {
  const latestEnd = Math.max(...props.modelValue.map((range) => range.end), 8 * 60);
  const start = latestEnd + 60;
  if (start >= LAST_MINUTE_OF_DAY) return null;
  return { start, end: Math.min(start + 3 * 60, LAST_MINUTE_OF_DAY) };
});

const handleTimeChange = (index: number, key: keyof TimeRange, value: unknown) => {
  // Ignore an emptied time input instead of storing an invalid range
  if (typeof value !== 'string' || !value) return;
  emit(
    'update:modelValue',
    props.modelValue.map((range, i) =>
      i === index ? { ...range, [key]: timeStringToMinuteOfDay(value) } : range,
    ),
  );
};

const handleAddRange = () => {
  if (!nextRange.value) return;
  emit('update:modelValue', [...props.modelValue, nextRange.value]);
};

const handleRemoveRange = (index: number) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
};
</script>

<i18n lang="json">
{
  "en": {
    "addRange": "+ Time slot",
    "closingBeforeOpening": "Closing time must be after opening time",
    "overlap": "Time slots must not overlap",
    "removeRange": "Remove time slot"
  },
  "de": {
    "addRange": "+ Zeitfenster",
    "closingBeforeOpening": "Die Schließzeit muss nach der Öffnungszeit liegen",
    "overlap": "Zeitfenster dürfen sich nicht überschneiden",
    "removeRange": "Zeitfenster entfernen"
  },
  "es": {
    "addRange": "+ Franja horaria",
    "closingBeforeOpening": "La hora de cierre debe ser posterior a la de apertura",
    "overlap": "Las franjas horarias no pueden solaparse",
    "removeRange": "Eliminar franja horaria"
  }
}
</i18n>
